<template>
  <v-card>
    <v-toolbar flat dense>
      <v-toolbar-title>Event Details</v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')"><v-icon>close</v-icon></v-btn>
    </v-toolbar>
    <v-divider />
    <v-card-text>
      <p class="subtitle-1">{{ event.name }}</p>
      <p><strong>Device:</strong> {{ event.device_name || event.device_guid }}</p>
      <p v-if="seriesAnchorStart && seriesAnchorStart !== event.start_time">
        <strong>Series anchor:</strong> {{ formatTime(seriesAnchorStart) }}
      </p>
      <p>
        <strong>Scheduled window:</strong>
        {{ formatTime(occurrenceStart || event.start_time) }}
        –
        {{ formatTime(occurrenceEnd || event.end_time) }}
      </p>
      <p v-if="recurrenceLabel"><strong>Recurrence:</strong> {{ recurrenceLabel }}</p>
      <p v-if="event.location"><strong>Location:</strong> {{ event.location }}</p>
      <p v-if="event.notes"><strong>Notes:</strong> {{ event.notes }}</p>
      <p v-if="event.pre_roll_seconds"><strong>Pre-roll:</strong> {{ event.pre_roll_seconds }}s</p>
      <p v-if="event.post_roll_seconds"><strong>Post-roll:</strong> {{ event.post_roll_seconds }}s</p>

      <scheduler-event-outputs :event="event" class="my-3" />

      <v-divider class="my-3" />

      <p class="subtitle-2">Test / preview</p>
      <v-btn
        v-if="device"
        small
        color="primary"
        outlined
        class="mr-2"
        :loading="toggleLoading"
        @click="toggleOutputs(true)"
      >
        Enable outputs (test)
      </v-btn>
      <v-btn
        v-if="device"
        small
        outlined
        :loading="toggleLoading"
        @click="toggleOutputs(false)"
      >
        Disable outputs
      </v-btn>

      <div v-if="event.device_guid" class="mt-3">
        <v-btn
          text
          small
          class="px-0"
          @click="inputPreviewExpanded = !inputPreviewExpanded"
        >
          <v-icon left small>
            {{ inputPreviewExpanded ? 'expand_less' : 'expand_more' }}
          </v-icon>
          {{ inputPreviewExpanded ? 'Hide input preview' : 'Show input preview' }}
        </v-btn>
        <v-expand-transition>
          <div v-if="inputPreviewExpanded" class="mt-2">
            <input-preview
              :key="event.start_time"
              :device_guid="event.device_guid"
            />
          </div>
        </v-expand-transition>
      </div>

      <div class="text-right mt-4" v-if="canEdit">
        <v-btn color="secondary" text @click="$emit('edit', event)">Edit</v-btn>
        <v-btn color="error" text @click="openDeleteDialog">Delete</v-btn>
      </div>
    </v-card-text>

    <v-dialog v-model="deleteDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">Delete scheduled event</v-card-title>
        <v-card-text>
          <p v-if="isSeriesDelete">
            This event is part of a recurring series. What should be removed?
          </p>
          <p v-else>Remove this scheduled event?</p>
          <v-radio-group v-if="isSeriesDelete" v-model="deleteScope" class="mt-2">
            <v-radio label="This occurrence only" value="this" />
            <v-radio label="This and all following occurrences" value="following" />
            <v-radio label="Entire series (all occurrences)" value="all" />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text :loading="deleteLoading" @click="confirmDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import InputPreview from '../device/components/InputPreview.vue'
import SchedulerEventOutputs from './SchedulerEventOutputs.vue'

export default {
  name: 'SchedulerEventDetail',

  components: { InputPreview, SchedulerEventOutputs },

  props: {
    event: { type: Object, required: true },
    canEdit: Boolean,
  },

  data() {
    return {
      toggleLoading: false,
      inputPreviewExpanded: false,
      deleteDialog: false,
      deleteScope: 'this',
      deleteLoading: false,
    }
  },

  watch: {
    'event.start_time'() {
      this.inputPreviewExpanded = false
    },
  },

  computed: {
    device() {
      if (!this.event.device_guid) return null
      return this.$devices.getDevice(this.event.device_guid)
    },

    occurrenceStart() {
      return this.event._occurrenceStart || null
    },

    occurrenceEnd() {
      return this.event._occurrenceEnd || null
    },

    recurrenceLabel() {
      if (this.event._recurrenceSummary) {
        return this.event._recurrenceSummary
      }
      const r = this.event.series_recurrence || this.event.recurrence
      if (!r || !r.frequency || r.frequency === 'none') {
        return null
      }
      return r.frequency
    },

    isSeriesDelete() {
      if (this.event.series_id) {
        return true
      }
      const r = this.event.recurrence
      return r && r.frequency && r.frequency !== 'none'
    },

    deleteStartTime() {
      return this.event._occurrenceStart || this.event.start_time
    },

    seriesAnchorStart() {
      return this.event.series_anchor_start_time || null
    },
  },

  methods: {
    formatTime(iso) {
      return this.$helpers.formatTimestamp(iso, null, 'lll')
    },

    async toggleOutputs(enable) {
      if (!this.device || !this.event.output_ids) return
      this.toggleLoading = true
      try {
        const payload = this.event.output_ids.map((id) => ({
          output_id: id,
          enable,
        }))
        await this.axios.post('/devices/' + this.event.device_guid + '/outputs', payload)
      } finally {
        this.toggleLoading = false
      }
    },

    openDeleteDialog() {
      this.deleteScope = 'this'
      this.deleteDialog = true
    },

    async confirmDelete() {
      const scope = this.isSeriesDelete ? this.deleteScope : 'this'
      this.deleteLoading = true
      try {
        await this.$scheduler.deleteEvent(
          this.event.device_guid,
          this.deleteStartTime,
          scope
        )
        this.deleteDialog = false
        this.$emit('deleted')
        this.$emit('close')
      } catch (err) {
        console.error(err)
        this.$emit('error', this.$helpers.parseError(err))
      } finally {
        this.deleteLoading = false
      }
    },
  },
}
</script>
